/**
 * The company-wide **required account-security level** (#282) as the SPA sees it, plus the German copy the
 * setting screen puts on the page.
 *
 * Mirrors `CheqMe.Server.Contracts/Authorization/AccountSecurityLevel.cs`. Two properties of that enum are
 * load-bearing here and are the reason this file exists rather than three string literals in a template:
 *
 * 1. **The wire format is the enum NAME, never its number.** `AccountController.SetCompanySecurityLevel` parses
 *    the body with `Enum.TryParse<AccountSecurityLevel>(request.RequiredLevel, ignoreCase: true, …)` and answers
 *    a bare `400` for anything it cannot parse. So `'TwoFactor'` is the contract — an ordinal would be rejected
 *    by a screen that looks perfectly correct.
 * 2. **The numbering is a RANKING, but only in one direction.** `AccountSecurityPolicy.Satisfies` is a plain
 *    `actual >= required`, which is what makes a federated account satisfy a `TwoFactor` requirement. It is NOT
 *    what makes `Federated` "more secure" as a *requirement*: as a requirement it forbids native accounts
 *    outright, which is a different decision rather than a stricter one. The copy below says so, because a
 *    dropdown ordered 0-1-2 invites exactly the opposite reading.
 */

import {readErrorCode} from '../../../../_auth/two-factor/second-factor-errors';

/**
 * The three levels, spelled as the **names the wire carries**.
 *
 * A string enum rather than a numeric one, deliberately: the value that leaves the browser is the value written
 * here, so there is no mapping step that could silently send `1`.
 */
export enum AccountSecurityLevel {
  /** A password and nothing else — the permissive default. */
  Password = 'Password',
  /** A password plus an enrolled authenticator, **or** any federated sign-in. */
  TwoFactor = 'TwoFactor',
  /** An external provider only; native e-mail + password accounts are refused outright. */
  Federated = 'Federated'
}

/** Request and response body of both `/auth/account/admin/companies/{companyId}/security-level` endpoints. */
export interface CompanySecurityRequirement {
  /** The {@link AccountSecurityLevel} name. `string` on purpose — a newer server may name a level this build has never heard of. */
  requiredLevel: string;
}

/**
 * One entry of the level picker.
 *
 * `cqme-select` is generic over `TItem extends object`, so the bare enum members need a wrapper — see the same
 * note on `ScopeOption` in `_modules/company/access/access-management.component.ts`.
 */
export interface AccountSecurityLevelOption {
  value: AccountSecurityLevel;
  label: string;
}

/** The short label of each level — what the picker shows. */
export const ACCOUNT_SECURITY_LEVEL_LABELS: Record<AccountSecurityLevel, string> = {
  [AccountSecurityLevel.Password]: 'Passwort',
  [AccountSecurityLevel.TwoFactor]: 'Passwort und zweiter Faktor',
  [AccountSecurityLevel.Federated]: 'Nur externe Anbieter'
};

/**
 * The picker's options, in the enum's own order.
 *
 * Ordered Password → TwoFactor → Federated because that is the ranking `Satisfies` compares on, and a list that
 * disagreed with the comparison would teach the wrong mental model. The "this is not simply stricter" caveat for
 * `Federated` is carried by {@link ACCOUNT_SECURITY_LEVEL_DESCRIPTIONS} and by the permanent note on the panel,
 * not by re-ordering the list.
 */
export const ACCOUNT_SECURITY_LEVEL_OPTIONS: AccountSecurityLevelOption[] = [
  {value: AccountSecurityLevel.Password, label: ACCOUNT_SECURITY_LEVEL_LABELS[AccountSecurityLevel.Password]},
  {value: AccountSecurityLevel.TwoFactor, label: ACCOUNT_SECURITY_LEVEL_LABELS[AccountSecurityLevel.TwoFactor]},
  {value: AccountSecurityLevel.Federated, label: ACCOUNT_SECURITY_LEVEL_LABELS[AccountSecurityLevel.Federated]}
];

/**
 * What each level means for the people who sign in — one sentence per level, sourced from the C# XML docs on
 * `AccountSecurityLevel` rather than paraphrased from the label.
 */
export const ACCOUNT_SECURITY_LEVEL_DESCRIPTIONS: Record<AccountSecurityLevel, string> = {
  [AccountSecurityLevel.Password]:
    'Ein Passwort genügt. Jedes Konto mit einer Berechtigung für dieses Unternehmen kommt herein — auch ohne '
    + 'Authenticator-App.',
  [AccountSecurityLevel.TwoFactor]:
    'Zum Passwort kommt eine Authenticator-App. Konten, die sich über Microsoft, Google oder Apple anmelden, '
    + 'erfüllen diese Stufe bereits und richten hier nichts ein.',
  [AccountSecurityLevel.Federated]:
    'Ausschließlich Anmeldungen über Microsoft, Google oder Apple. Konten mit E-Mail und Passwort werden '
    + 'abgewiesen — auch dann, wenn sie eine Authenticator-App eingerichtet haben.'
};

/**
 * Whether `value` is a level this build knows.
 *
 * The server sends a name, and `requiredLevel` is typed `string` for that reason: a level added later must not
 * be silently rendered as an empty picker. An unknown name is treated as "nothing selected" by the panel, which
 * shows the raw name beside a note instead of pretending the company is on `Password`.
 */
export function isKnownAccountSecurityLevel(value: string | null | undefined): value is AccountSecurityLevel {
  return value === AccountSecurityLevel.Password
    || value === AccountSecurityLevel.TwoFactor
    || value === AccountSecurityLevel.Federated;
}

/**
 * Whether moving from `from` to `to` **raises** the bar.
 *
 * Compares the position in {@link ACCOUNT_SECURITY_LEVEL_OPTIONS}, which is the enum's own numbering — the same
 * ranking `AccountSecurityPolicy.Satisfies` compares on. Used only to pick which consequence the confirmation
 * dialog names; it is not an authorization decision.
 */
export function raisesRequirement(from: AccountSecurityLevel, to: AccountSecurityLevel): boolean {
  return rank(to) > rank(from);
}

function rank(level: AccountSecurityLevel): number {
  return ACCOUNT_SECURITY_LEVEL_OPTIONS.findIndex(o => o.value === level);
}

/**
 * The German fallback for a refusal this screen does not recognise.
 *
 * Deliberate, not a gap: the style guide's "Surfacing an API error" rule forbids ever rendering the backend's
 * English `description`, so an unmapped code has to degrade to a fixed German sentence.
 */
export const ACCOUNT_SECURITY_ADMIN_FALLBACK_MESSAGE =
  'Die Anmeldesicherheit konnte nicht gespeichert werden. Bitte versuchen Sie es später erneut.';

/** The same, for the initial read — a different action, so a different sentence. */
export const ACCOUNT_SECURITY_ADMIN_LOAD_FALLBACK_MESSAGE =
  'Die aktuelle Einstellung konnte nicht geladen werden. Bitte laden Sie die Seite neu.';

/**
 * German sentences for the refusals these two endpoints can actually produce.
 *
 * **Both of them answer largely WITHOUT a structured `ApiError` body**, which is why this maps HTTP status as
 * well as code:
 *
 * - `403` — from `[Authorize(Roles = PlatformOwner, CompanyOwner)]` or from the controller's private
 *   `IsOwnCompanyOrPlatform(companyId)` check. `Forbid()` writes **no body at all**.
 * - `400` — `Enum.TryParse` rejected the name. `BadRequest()`, again with no body.
 * - `404` — `AccountSecondFactorService.SetRequirement` did not find the company:
 *   `ResponseModel<object>.NotFound(ResponseErrors.NotFound)` carries no `ErrorCode`, so
 *   `ResponseModelExtensions.BuildResponse` takes its *non*-error branch and the body is a bare legacy enum
 *   value rather than `{code, description, statusCode}`.
 *
 * So a screen that only branched on `error.code` would show its generic fallback for every realistic failure,
 * including the one an operator most needs named (403 — "this account may not change it").
 */
const STATUS_MESSAGES: Record<number, string> = {
  0: 'Der Server ist nicht erreichbar. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
  400: 'Die gewählte Stufe wurde vom Server nicht akzeptiert. Bitte laden Sie die Seite neu.',
  401: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
  403: 'Dieses Konto darf die Anmeldesicherheit dieses Unternehmens nicht ändern. Erforderlich ist die Rolle '
    + '„Unternehmensinhaber“ oder „Plattforminhaber“.',
  404: 'Das Unternehmen wurde nicht gefunden. Bitte laden Sie die Seite neu.'
};

/** The handful of structured codes that could reach this admin surface, with their German sentence. */
const CODE_MESSAGES: Record<string, string> = {
  // Not raised by these two endpoints today, but every /auth route sits behind the account-security gate, so a
  // restricted session's 403 can arrive here. Naming it beats the 403 sentence above, which would send an
  // operator looking for a role they already hold.
  E_AUTH_ACCOUNT_SECURITY_RESTRICTED:
    'Ihre eigene Sitzung ist eingeschränkt, bis Sie eine Authenticator-App eingerichtet haben. Bitte holen Sie '
    + 'das zuerst nach.',
  E_AUTH_ACCOUNT_SECURITY_LEVEL_UNREACHABLE:
    'Ihr eigenes Konto erfüllt die Anmeldesicherheit dieses Unternehmens nicht mehr.'
};

/**
 * The German sentence for a failed call on this panel.
 *
 * Order is code → status → fallback, because a code is the more specific statement of the two whenever one is
 * present. `readErrorCode` is reused from `_auth/two-factor/second-factor-errors.ts` rather than re-implemented:
 * it already reads `err.error.code` defensively, and two copies of "how do I find the code" is how one of them
 * ends up reading the wrong field.
 *
 * @param err       whatever rxjs handed the `error` callback.
 * @param fallback  the sentence for a failure with neither a known code nor a mapped status.
 */
export function describeAccountSecurityAdminError(
  err: unknown,
  fallback: string = ACCOUNT_SECURITY_ADMIN_FALLBACK_MESSAGE): string {

  const code = readErrorCode(err);
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];

  const status = (err as {status?: unknown} | null)?.status;
  if (typeof status === 'number' && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];

  return fallback;
}
